import { Poller } from "./poller";

type PollerPoolOptions = {
  interval?: number;
  onError?: (e: unknown) => void;
  onLoadingChange?: (isLoading: boolean) => void;
};

export default class PollerPool {
  private pollers = new Map<string, Poller<unknown>>();
  private pollInterval: NodeJS.Timeout | null = null;
  private payload: unknown;
  private intervalMs: number;

  constructor(private options: PollerPoolOptions = {}) {
    this.intervalMs = (options.interval || 0) * 1000;
  }

  private async executePoll(payload?: unknown) {
    if (!this.pollers.size) return;

    try {
      this.setLoading(true);
      const pollers = Array.from(this.pollers.values());
      pollers.forEach(p => p.doAsyncWithArg(payload ?? this.payload));
    } catch (error) {
      this.options.onError?.(error);
    } finally {
      this.setLoading(false);
    }
  }

  registerPoller(id: string, poller: Poller) {
    poller && this.pollers.set(id, poller);
  }

  registerPollerIfNotExists(id: string, poller: Poller) {
    const exist = this.pollers.get(id);
    if (!exist) this.registerPoller(id, poller);
  }

  startPolling(interval?: number, payload?: unknown) {
    this.stopPolling();
    this.payload = payload;

    if (interval) {
      this.intervalMs = interval * 1000;
    }

    this.executePoll(payload);
    this.pollInterval = setInterval(
      () => this.executePoll(payload),
      this.intervalMs
    );
  }

  async pollOnce(payload?: unknown) {
    this.stopPolling();
    this.payload = payload;
    await this.executePoll(payload);
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  private setLoading(isLoading: boolean) {
    this.options.onLoadingChange?.(isLoading);
  }

  setPollerPaused(id: string, pause: boolean) {
    const poller = this.pollers.get(id);
    poller?.setPaused(pause);
    if (poller?.needsUpdate()) poller.doAsyncWithArg(this.payload);
  }
}
